import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { FaGhost, FaBars, FaTimes, FaSignOutAlt, FaCog, FaMoon, FaSun, FaSearch, FaStream, FaBookmark, FaChevronDown, FaPlus, FaBook, FaEllipsisH, FaTrophy, FaCalculator } from 'react-icons/fa';
import NotificationBell from './NotificationBell';
import { getContentTypeIcon } from '../utils/designTokens';

const Navbar = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [browseDropdownOpen, setBrowseDropdownOpen] = useState(false);
  const [createDropdownOpen, setCreateDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);

  const browseDropdownRef = useRef(null);
  const createDropdownRef = useRef(null);
  const moreDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (browseDropdownRef.current && !browseDropdownRef.current.contains(event.target)) {
        setBrowseDropdownOpen(false);
      }
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setCreateDropdownOpen(false);
      }
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setMoreDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="bg-white dark:bg-spooky-bg-dark shadow-md dark:shadow-spooky-purple-mist/20 sticky top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <FaGhost className="text-3xl text-boop-primary animate-float" />
            <span className="text-2xl font-bold bg-gradient-to-r from-boop-primary to-boop-secondary bg-clip-text text-transparent">
              Boop
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Browse Dropdown */}
            <div className="relative" ref={browseDropdownRef}>
              <button
                onClick={() => setBrowseDropdownOpen(!browseDropdownOpen)}
                className="flex items-center gap-1 text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition"
              >
                Browse <FaChevronDown className="text-xs" />
              </button>
              {browseDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    onClick={() => setBrowseDropdownOpen(false)}
                  >
                    <span className="text-lg">🏠</span>
                    <span>All Content</span>
                  </Link>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  <Link
                    to="/dashboard?type=prompt"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition"
                    onClick={() => setBrowseDropdownOpen(false)}
                  >
                    <span className="text-lg">{getContentTypeIcon('prompt')}</span>
                    <span>Prompts</span>
                  </Link>
                  <Link
                    to="/dashboard?type=agent"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition"
                    onClick={() => setBrowseDropdownOpen(false)}
                  >
                    <span className="text-lg">{getContentTypeIcon('agent')}</span>
                    <span>AI Agents</span>
                  </Link>
                  <Link
                    to="/dashboard?type=mcp_server"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition"
                    onClick={() => setBrowseDropdownOpen(false)}
                  >
                    <span className="text-lg">{getContentTypeIcon('mcp_server')}</span>
                    <span>MCP Servers</span>
                  </Link>
                </div>
              )}
            </div>
            <Link to="/how-to-use" className="flex items-center gap-1.5 text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition">
              <FaBook /> Guide
            </Link>
            <Link to="/search" className="flex items-center gap-1.5 text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition">
              <FaSearch /> Search
            </Link>
            {currentUser && (
              <Link to="/collections" className="flex items-center gap-1.5 text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition">
                <FaBookmark /> Collections
              </Link>
            )}

            {/* More Dropdown */}
            <div className="relative" ref={moreDropdownRef}>
              <button
                onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                className="flex items-center gap-1 text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition"
              >
                <FaEllipsisH /> <FaChevronDown className="text-xs" />
              </button>
              {moreDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                  <Link
                    to="/activity"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    onClick={() => setMoreDropdownOpen(false)}
                  >
                    <FaStream />
                    <span>Activity</span>
                  </Link>
                  <Link
                    to="/leaderboard"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    onClick={() => setMoreDropdownOpen(false)}
                  >
                    <FaTrophy />
                    <span>Leaderboard</span>
                  </Link>
                  <Link
                    to="/token-analyzer"
                    className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    onClick={() => setMoreDropdownOpen(false)}
                  >
                    <FaCalculator />
                    <span>Token Analyzer</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition text-xl"
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <FaSun className="animate-glow" /> : <FaMoon />}
            </button>

            {currentUser && userProfile ? (
              <div className="flex items-center space-x-4">
                {/* Create Dropdown */}
                <div className="relative" ref={createDropdownRef}>
                  <button
                    onClick={() => setCreateDropdownOpen(!createDropdownOpen)}
                    className="flex items-center gap-2 bg-boop-primary text-white px-4 py-2 rounded-lg hover:bg-boop-secondary transition"
                  >
                    <FaPlus /> Create <FaChevronDown className="text-xs" />
                  </button>
                  {createDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                      <Link
                        to="/prompts/create"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition"
                        onClick={() => setCreateDropdownOpen(false)}
                      >
                        <span className="text-lg">{getContentTypeIcon('prompt')}</span>
                        <span>Create Prompt</span>
                      </Link>
                      <Link
                        to="/agents/create"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition"
                        onClick={() => setCreateDropdownOpen(false)}
                      >
                        <span className="text-lg">{getContentTypeIcon('agent')}</span>
                        <span>Create AI Agent</span>
                      </Link>
                      <Link
                        to="/mcp-servers/create"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition"
                        onClick={() => setCreateDropdownOpen(false)}
                      >
                        <span className="text-lg">{getContentTypeIcon('mcp_server')}</span>
                        <span>Create MCP Server</span>
                      </Link>
                    </div>
                  )}
                </div>
                {['admin','system','moderator'].includes(userProfile.role) && (
                  <Link to="/admin" className="text-gray-700 dark:text-spooky-ghost-white hover:text-boop-primary dark:hover:text-spooky-ghost-glow transition">
                    Admin
                  </Link>
                )}
                <div className="flex items-center space-x-2">
                  {/* Notification Bell */}
                  <NotificationBell />

                  <Link
                    to={`/profile/${userProfile.username}`}
                    className="flex items-center space-x-2 hover:opacity-80 transition"
                  >
                    <span className={`level-badge level-${userProfile.level}`}>
                      {userProfile.level}
                    </span>
                    <span className="text-gray-700 dark:text-spooky-ghost-white">{userProfile.username}</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="text-gray-500 hover:text-boop-primary transition"
                    title="Settings"
                  >
                    <FaCog />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-red-500 transition"
                    title="Logout"
                  >
                    <FaSignOutAlt />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-boop-primary transition"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-boop-primary text-white px-4 py-2 rounded-lg hover:bg-boop-secondary transition"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-boop-primary transition"
            >
              {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t dark:border-gray-700">
            <div className="flex flex-col space-y-3">
              {/* Mobile Browse Section */}
              <div className="px-2 py-1 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Browse
              </div>
              <Link
                to="/dashboard"
                className="text-gray-700 dark:text-gray-300 hover:text-boop-primary transition flex items-center gap-2 px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>🏠</span> All Content
              </Link>
              <Link
                to="/dashboard?type=prompt"
                className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 transition flex items-center gap-2 px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{getContentTypeIcon('prompt')}</span> Prompts
              </Link>
              <Link
                to="/dashboard?type=agent"
                className="text-gray-700 dark:text-gray-300 hover:text-pink-600 transition flex items-center gap-2 px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{getContentTypeIcon('agent')}</span> AI Agents
              </Link>
              <Link
                to="/dashboard?type=mcp_server"
                className="text-gray-700 dark:text-gray-300 hover:text-cyan-600 transition flex items-center gap-2 px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{getContentTypeIcon('mcp_server')}</span> MCP Servers
              </Link>

              <div className="border-t dark:border-gray-700 my-2"></div>
              <Link
                to="/how-to-use"
                className="text-gray-700 dark:text-gray-300 hover:text-boop-primary transition font-medium px-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                📚 How to Use
              </Link>
              <Link
                to="/search"
                className="text-gray-700 hover:text-boop-primary transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Search
              </Link>
              <Link
                to="/activity"
                className="text-gray-700 hover:text-boop-primary transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Activity
              </Link>
              {currentUser && (
                <Link
                  to="/collections"
                  className="text-gray-700 hover:text-boop-primary transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Collections
                </Link>
              )}
              <Link
                to="/leaderboard"
                className="text-gray-700 hover:text-boop-primary transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Leaderboard
              </Link>
              <Link
                to="/token-analyzer"
                className="text-gray-700 hover:text-boop-primary transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Token Analyzer
              </Link>

              {currentUser && userProfile ? (
                <>
                  {/* Mobile Create Section */}
                  <div className="px-2 py-1 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mt-2">
                    Create
                  </div>
                  <Link
                    to="/prompts/create"
                    className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 transition flex items-center gap-2 px-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{getContentTypeIcon('prompt')}</span> Create Prompt
                  </Link>
                  <Link
                    to="/agents/create"
                    className="text-gray-700 dark:text-gray-300 hover:text-pink-600 transition flex items-center gap-2 px-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{getContentTypeIcon('agent')}</span> Create AI Agent
                  </Link>
                  <Link
                    to="/mcp-servers/create"
                    className="text-gray-700 dark:text-gray-300 hover:text-cyan-600 transition flex items-center gap-2 px-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{getContentTypeIcon('mcp_server')}</span> Create MCP Server
                  </Link>
                  <div className="border-t dark:border-gray-700 my-2"></div>
                  {['admin','system','moderator'].includes(userProfile.role) && (
                    <Link
                      to="/admin"
                      className="text-gray-700 hover:text-boop-primary transition"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin
                    </Link>
                  )}
                  <Link
                    to={`/profile/${userProfile.username}`}
                    className="text-gray-700 hover:text-boop-primary transition"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="text-gray-700 hover:text-boop-primary transition"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-red-500 hover:text-red-600 transition text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-boop-primary transition"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-boop-primary text-white px-4 py-2 rounded-lg hover:bg-boop-secondary transition text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
